import React, { useEffect, useState } from 'react';
import { Save, AlertCircle, RefreshCw, CheckCircle2 } from 'lucide-react';
import { endpoints } from '../../api/api';

const SmartEmailSettings = ({ darkMode, onClose }) => {
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [status, setStatus] = useState(null);

    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        setLoading(true);
        try {
            const res = await endpoints.drafts.getSettings();
            setSettings(res.data);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        setStatus(null);
        try {
            const res = await endpoints.drafts.updateSettings(settings);
            setSettings(res.data);
            setStatus({ type: 'success', message: 'Settings saved' });
            setTimeout(() => setStatus(null), 3000);
        } catch (err) {
            setStatus({ type: 'error', message: 'Failed to save' });
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div className="p-8 text-center"><RefreshCw className="animate-spin mx-auto opacity-50" /></div>;

    return (
        <div className={`h-full flex flex-col ${darkMode ? 'text-white' : 'text-slate-900'}`}>
            <div className={`p-4 border-b flex items-center justify-between ${darkMode ? 'border-white/10' : 'border-slate-100'}`}>
                <h3 className="font-semibold text-sm">Autonomous Settings</h3>
                <button onClick={onClose} className="text-xs opacity-50 hover:opacity-100">Close</button>
            </div>

            <form onSubmit={handleSave} className="flex-1 overflow-y-auto p-4 space-y-6">

                {/* Mode */}
                <div className="space-y-2">
                    <label className="text-xs font-semibold uppercase tracking-wide opacity-70">Approval Mode</label>
                    <div className="grid grid-cols-3 gap-2">
                        {['auto', 'quick', 'full'].map(mode => (
                            <button
                                key={mode}
                                type="button"
                                onClick={() => setSettings({ ...settings, default_approval_mode: mode })}
                                className={`py-2 px-1 text-xs font-medium rounded border transition-colors capitalize ${settings.default_approval_mode === mode
                                        ? 'bg-blue-600 text-white border-transparent'
                                        : darkMode ? 'border-white/10 hover:bg-white/5' : 'border-slate-200 hover:bg-slate-50'
                                    }`}
                            >
                                {mode} Check
                            </button>
                        ))}
                    </div>
                </div>

                {/* Tone */}
                <div className="space-y-2">
                    <label className="text-xs font-semibold uppercase tracking-wide opacity-70">Tone Preference</label>
                    <select
                        value={settings.tone_preference}
                        onChange={(e) => setSettings({ ...settings, tone_preference: e.target.value })}
                        className={`w-full p-2 rounded border text-sm ${darkMode ? 'bg-slate-800 border-white/10' : 'bg-white border-slate-200'}`}
                    >
                        <option value="professional">Professional</option>
                        <option value="casual">Casual</option>
                        <option value="friendly">Friendly</option>
                        <option value="direct">Direct</option>
                    </select>
                </div>

                {/* Whitelist */}
                <div className="space-y-2">
                    <label className="text-xs font-semibold uppercase tracking-wide opacity-70">Auto-Send Whitelist (Domains)</label>
                    <p className="text-[10px] opacity-60">Emails to these domains can be auto-sent if confidence {'>'} 90%.</p>
                    <textarea
                        value={settings.auto_send_whitelist_domains.join(', ')}
                        onChange={(e) => setSettings({
                            ...settings,
                            auto_send_whitelist_domains: e.target.value.split(',').map(s => s.trim()).filter(Boolean)
                        })}
                        placeholder="example.com, company.com"
                        className={`w-full p-2 h-20 rounded border text-sm ${darkMode ? 'bg-slate-800 border-white/10' : 'bg-white border-slate-200'}`}
                    />
                </div>

                {/* Safety */}
                <div className="space-y-2">
                    <label className="text-xs font-semibold uppercase tracking-wide opacity-70">Daily Limit</label>
                    <input
                        type="number"
                        value={settings.daily_auto_send_limit}
                        onChange={(e) => setSettings({ ...settings, daily_auto_send_limit: parseInt(e.target.value) })}
                        className={`w-full p-2 rounded border text-sm ${darkMode ? 'bg-slate-800 border-white/10' : 'bg-white border-slate-200'}`}
                    />
                </div>

            </form>

            <div className={`p-4 border-t ${darkMode ? 'border-white/10' : 'border-slate-100'}`}>
                <button
                    onClick={handleSave}
                    disabled={saving}
                    className="w-full py-2 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-lg text-sm font-medium flex items-center justify-center gap-2"
                >
                    {saving ? <RefreshCw className="animate-spin w-4 h-4" /> : <Save className="w-4 h-4" />}
                    Save Changes
                </button>
                {status && (
                    <div className={`mt-2 text-xs flex items-center justify-center gap-1 ${status.type === 'error' ? 'text-red-500' : 'text-emerald-500'}`}>
                        {status.type === 'success' && <CheckCircle2 size={12} />}
                        {status.message}
                    </div>
                )}
            </div>
        </div>
    );
};

export default SmartEmailSettings;
